"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const contactMethods = [{
  icon: Mail,
  title: "EMAIL US",
  detail: "hello@propvr.ai",
  link: "mailto:hello@propvr.ai"
}, {
  icon: Phone,
  title: "CALL US",
  detail: "+1 (800) PROP-VR",
  link: "tel:1800776787"
}, {
  icon: MapPin,
  title: "VISIT US",
  detail: "Global Digital Headquarters",
  link: "#"
}];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="89797" className="flex flex-col items-center justify-center p-8 text-center bg-[#1755FD]/10 border border-[#1755FD]/20 rounded-xl">
        <div className="size-16 bg-[#1755FD]/20 rounded-full flex items-center justify-center mb-4">
          <Mail className="size-8 text-[#1755FD]" />
        </div>
        <h3 className="text-2xl font-default font-bold text-[#0E0C15] mb-2">Message Sent!</h3>
        <p className="text-[#5E5E5E] mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-[#0E0C15] text-[#0E0C15] hover:bg-[#0E0C15] hover:text-white transition-colors duration-300">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" rules={{
          required: "Your name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[#0E0C15] font-medium font-default">Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" className="bg-white border-[#5E5E5E] text-[#02030B] rounded-xl" {...field} value={field.value || ''} />
                </FormControl>
                <FormDescription className="text-[#5E5E5E]/70">First and last name</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[#0E0C15] font-medium font-default">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@propvr.ai" className="bg-white border-[#5E5E5E] text-[#02030B] rounded-xl" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[0-9+\-\s()]*$/,
            message: "Please enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[#0E0C15] font-medium font-default">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-white border-[#5E5E5E] text-[#02030B] rounded-xl" {...field} value={field.value || ''} />
                </FormControl>
                <FormDescription className="text-[#5E5E5E]/70">Optional – we may call you back</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="subject" rules={{
          required: "Please select a subject"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-[#0E0C15] font-medium font-default">Subject</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-white border-[#5E5E5E] text-[#02030B] rounded-xl">
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white border-[#E5E7EB]">
                    <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                    <SelectItem value="3D Rendering">3D Rendering Services</SelectItem>
                    <SelectItem value="VR Experience">VR Experience Setup</SelectItem>
                    <SelectItem value="Partnership">Partnership Opportunities</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" rules={{
        required: "Please enter your message"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#0E0C15] font-medium font-default">Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your project or inquiry..." className="min-h-[150px] resize-y bg-white border-[#5E5E5E] text-[#02030B] rounded-xl" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-default">
            {submitError}
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:px-8 py-6 text-base bg-[#0E0C15] hover:bg-[#1A1726] text-white rounded-xl transition-all duration-300 shadow-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending Message...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}
export default function ContactTheLndryCo() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="start-your-vr-journey" className="w-full bg-[#0E0C15] py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden dark">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16 md:mb-24">
          <span className="text-[#1755FD] font-default text-sm font-bold uppercase tracking-widest mb-4 block">
            Begin Your Journey
          </span>
          <h2 className="font-default text-5xl md:text-7xl font-bold text-white mb-6 uppercase tracking-tight">
            Start Your VR Journey
          </h2>
          <p className="text-[#9CA3AF] font-default text-lg md:text-xl max-w-2xl mx-auto">
            Experience the future of spatial intelligence. Our team is ready to help you transform how you showcase properties across the globe.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {contactMethods.map((method, i) => <div data-index={i} key={i}>
              <Link to={method.link} className="block h-full group">
                <Card className="h-full bg-[#1A1726] border border-[#2D2B35] shadow-sm hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 rounded-xl">
                  <CardContent className="flex flex-col items-center justify-center p-10 text-center h-full">
                    <div className="size-24 bg-[#0E0C15] rounded-2xl flex items-center justify-center mb-8 group-hover:bg-[#1755FD]/20 transition-colors duration-300 border border-[#2D2B35]">
                      <method.icon className="size-12 text-[#1755FD]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-default text-xl font-bold text-white mb-3 uppercase tracking-wide">
                      {method.title}
                    </h3>
                    <p className="font-default text-lg text-[#9CA3AF] group-hover:text-[#1755FD] transition-colors duration-300">
                      {method.detail}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </div>)}
        </div>

        <div className="max-w-4xl mx-auto bg-[#F9FAFB] rounded-xl shadow-2xl border border-[#E5E7EB] p-8 md:p-12">
          <div className="mb-8">
            <h3 className="font-default text-3xl md:text-4xl font-bold text-[#0E0C15] mb-2">
              Send us a message
            </h3>
            <p className="text-[#5E5E5E] font-default">
              Ready to step into the Metaverse? Fill out the details below and our team will get back to you within 24 hours.
            </p>
          </div>

          <FormProvider formKey="start_vr_journey_form_prop_v_rai_redesign" formId="start_vr_journey_form_prop_v_rai_redesign" sectionName="Start Your VR Journey" defaultValues={{
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          subject: {
            required: true
          },
          message: {
            required: true
          }
        }} submitText="Send Message" successMessage="Thank you for reaching out! We have received your message and will contact you shortly to begin your VR transformation.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}